/**
 * usePlaybackPreferences — tiny hook that loads the user's persisted
 * "last-used speed" for each playback surface and persists changes back
 * to the server (debounced). Used in Reader.jsx (Flash / Chunk / TTS) and
 * Player.jsx.
 *
 * Returns
 *   {
 *     prefs,                 // { flash_wpm, chunk_ms, tts_speed, player_speed }
 *                            // each value is null until the user has
 *                            // customized it.
 *     loaded,                // true once the initial GET resolves
 *     savePref(field, value) // patches the field; debounced 600ms
 *   }
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const DEBOUNCE_MS = 600;

const VALID_FIELDS = new Set(['flash_wpm', 'chunk_ms', 'tts_speed', 'player_speed']);

export const usePlaybackPreferences = () => {
  const [prefs, setPrefs] = useState({
    flash_wpm: null,
    chunk_ms: null,
    tts_speed: null,
    player_speed: null,
  });
  const [loaded, setLoaded] = useState(false);
  const timersRef = useRef({}); // { fieldName: timeoutId }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/preferences/playback-speeds`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setPrefs((p) => ({ ...p, ...data }));
      } catch {
        // Network error — fall back to defaults; not worth surfacing.
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  const savePref = useCallback((field, value) => {
    if (!VALID_FIELDS.has(field) || value === null || value === undefined) return;
    // Optimistic update so the "Your usual" badge dissolves immediately.
    setPrefs((p) => ({ ...p, [field]: value }));
    // Debounce the network write per-field — dragging a slider would
    // otherwise fire a PATCH per tick.
    if (timersRef.current[field]) clearTimeout(timersRef.current[field]);
    timersRef.current[field] = setTimeout(async () => {
      try {
        await authFetch(`${BACKEND_URL}/api/preferences/playback-speeds`, {
          method: 'PATCH',
          body: JSON.stringify({ [field]: value }),
        });
      } catch {
        // Best-effort persistence — UX shouldn't break on network blips.
      }
    }, DEBOUNCE_MS);
  }, []);

  // Cleanup pending debounced writes on unmount.
  useEffect(() => () => {
    Object.values(timersRef.current).forEach((id) => id && clearTimeout(id));
  }, []);

  return { prefs, loaded, savePref };
};

export default usePlaybackPreferences;
